import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * useCmdbChain
 * Generic chaining controller for CMDB (Assets) dependent fields.
 *
 * Concurrency model:
 * - In-flight dedup: identical concurrent requests (same field + same signature)
 *   collapse into a single network call instead of bursting.
 * - Last-write-wins: each request for a field carries a monotonic token; only the
 *   latest token may apply its result. Superseded responses are dropped.
 * - Safe failures: an aborted/failed request never overwrites existing good options,
 *   so the UI can't collapse to an empty (and thus disabled) state.
 */

export interface CmdbOption {
  value: string;
  label: string;
  key?: string;
  objectKey?: string;
  attributes?: unknown[];
}

type CmdbValue = CmdbOption | null | undefined;
type Selections = Record<string, CmdbValue>;

export interface CmdbUserCtx {
  sectors?: string[];
  role?: string;
  accountId?: string | null;
}

export type CmdbFetcher = (
  fieldId: string,
  selections: Selections,
  userCtx?: CmdbUserCtx,
) => Promise<CmdbOption[]>;

export interface UseCmdbChainParams {
  fieldsOrder: string[];
  getCurrentValue: (fieldId: string) => CmdbValue;
  setValue: (fieldId: string, option: CmdbOption | null) => void;
  clearValue: (fieldId: string) => void;
  fetcher: CmdbFetcher;
  userCtx?: CmdbUserCtx;
  autoSelectSingle?: boolean;
}

export interface UseCmdbChainApi {
  optionsByField: Record<string, CmdbOption[]>;
  loadingField: string | null;
  loadOptionsFor: (fieldId: string, overrideSelections?: Selections) => Promise<void>;
  handleSelect: (fieldId: string, option: CmdbOption | null) => Promise<void>;
  clearAfter: (fieldId: string) => void;
  enabledByField: Record<string, boolean>;
  isFieldEnabled: (fieldId: string) => boolean;
}

export function useCmdbChain({
  fieldsOrder,
  getCurrentValue,
  setValue,
  clearValue,
  fetcher,
  userCtx,
  autoSelectSingle = true,
}: UseCmdbChainParams): UseCmdbChainApi {
  const [optionsByField, setOptionsByField] = useState<Record<string, CmdbOption[]>>({});
  const [loadingField, setLoadingField] = useState<string | null>(null);

  const orderRef = useRef<string[]>(fieldsOrder || []);
  orderRef.current = fieldsOrder || [];

  // Refs read inside loadOptionsFor so its identity stays stable across renders.
  const optionsByFieldRef = useRef<Record<string, CmdbOption[]>>(optionsByField);
  optionsByFieldRef.current = optionsByField;
  const userCtxRef = useRef<CmdbUserCtx | undefined>(userCtx);
  userCtxRef.current = userCtx;

  const lastSigRef = useRef<Record<string, string>>({});
  // Per-field in-flight request, keyed by field id; deduplicates identical calls.
  const inFlightRef = useRef<Record<string, { signature: string; promise: Promise<void> }>>({});
  // Per-field monotonic request token; only the latest token may apply its result.
  const reqSeqRef = useRef<Record<string, number>>({});

  const fieldIndex = useCallback((fieldId: string) => orderRef.current.indexOf(fieldId), []);

  const selectionsUpTo = useCallback((fieldId: string): Selections => {
    const idx = fieldIndex(fieldId);
    const map: Selections = {};
    if (idx <= 0) return map;
    for (let i = 0; i < idx; i += 1) {
      const fid = orderRef.current[i];
      const v = getCurrentValue(fid);
      if (v && (v.value || v.label)) map[fid] = v;
    }
    return map;
  }, [fieldIndex, getCurrentValue]);

  const clearOptionsAfter = useCallback((fieldId: string) => {
    const idx = fieldIndex(fieldId);
    if (idx < 0) return;
    setOptionsByField((prev) => {
      const copy = { ...prev };
      for (let i = idx + 1; i < orderRef.current.length; i += 1) {
        delete copy[orderRef.current[i]];
      }
      return copy;
    });
    // also clear cached signatures for subsequent fields
    for (let i = idx + 1; i < orderRef.current.length; i += 1) {
      delete lastSigRef.current[orderRef.current[i]];
    }
  }, [fieldIndex]);

  const clearAfter = useCallback((fieldId: string) => {
    const idx = fieldIndex(fieldId);
    if (idx < 0) return;
    for (let i = idx + 1; i < orderRef.current.length; i += 1) {
      clearValue(orderRef.current[i]);
    }
    clearOptionsAfter(fieldId);
  }, [clearValue, clearOptionsAfter, fieldIndex]);

  const buildSignature = useCallback((fieldId: string, selections?: Selections) => {
    const keys = Object.keys(selections || {}).sort();
    const sel = keys
      .map((k) => `${k}:${selections?.[k]?.label || selections?.[k]?.value || ''}`)
      .join('|');
    const uc = userCtxRef.current;
    const role = uc?.role || '';
    const sectors = Array.isArray(uc?.sectors) ? uc.sectors.join(',') : '';
    return `${fieldId}::${sel}::${role}::${sectors}`;
  }, []);

  const loadOptionsFor = useCallback(
    async (fieldId: string, overrideSelections?: Selections): Promise<void> => {
      if (!fieldId) return;

      const prevSelections = overrideSelections || selectionsUpTo(fieldId);
      const signature = buildSignature(fieldId, prevSelections);

      // Already fetched for this exact signature (even an empty result): skip.
      const existing = optionsByFieldRef.current[fieldId];
      if (existing !== undefined && lastSigRef.current[fieldId] === signature) {
        return;
      }

      // An identical request is already running: reuse it instead of bursting.
      const inFlight = inFlightRef.current[fieldId];
      if (inFlight && inFlight.signature === signature) {
        return inFlight.promise;
      }

      // Claim the latest-request token for this field.
      const token = (reqSeqRef.current[fieldId] = (reqSeqRef.current[fieldId] || 0) + 1);

      const run = async (): Promise<void> => {
        try {
          setLoadingField(fieldId);

          const fetched = await fetcher(fieldId, prevSelections, userCtxRef.current);

          // A newer request for this field superseded us: drop this result.
          if (reqSeqRef.current[fieldId] !== token) return;

          const current = getCurrentValue(fieldId);
          const merged: CmdbOption[] = current
            ? [current, ...(Array.isArray(fetched) ? fetched : [])]
            : Array.isArray(fetched)
              ? fetched
              : [];

          const seen = new Set<string>();
          const dedup: CmdbOption[] = [];
          for (const o of merged) {
            const key = o && o.value ? String(o.value) : '';
            if (!key || seen.has(key)) continue;
            seen.add(key);
            dedup.push({
              value: String(o.value),
              label: String(o.label),
              objectKey: o.key,
              key: o.key,
              attributes: o.attributes || [],
            });
          }

          setOptionsByField((prev) => ({ ...prev, [fieldId]: dedup }));
          lastSigRef.current[fieldId] = signature;

          if (autoSelectSingle && dedup.length === 1 && (!current || current.value !== dedup[0].value)) {
            const autoOpt = dedup[0];
            setValue(fieldId, autoOpt);
            // Selection changed: clear subsequent fields.
            clearAfter(fieldId);
            // Preload next field with up-to-date selections including this auto selection.
            const idx = fieldIndex(fieldId);
            const nextId = idx >= 0 ? orderRef.current[idx + 1] : null;
            if (nextId) {
              // IMPORTANT: propagate the selections used for this fetch,
              // not the possibly stale state-based selections.
              const nextSelections: Selections = { ...(prevSelections || {}) };
              nextSelections[fieldId] = autoOpt;
              await loadOptionsFor(nextId, nextSelections);
            }
          }
        } catch (err: any) {
          // Aborts are expected control-flow and must never touch options.
          if (err?.name === 'AbortError') return;
          // A failed request must not clobber good data: only fall back to []
          // when we are the latest request AND there is no existing data.
          if (reqSeqRef.current[fieldId] === token) {
            const existingNow = optionsByFieldRef.current[fieldId];
            if (existingNow === undefined || (Array.isArray(existingNow) && existingNow.length === 0)) {
              setOptionsByField((prev) => ({ ...prev, [fieldId]: [] }));
            }
          }
        } finally {
          setLoadingField((curr) => (curr === fieldId ? null : curr));
          const entry = inFlightRef.current[fieldId];
          if (entry && entry.signature === signature) {
            delete inFlightRef.current[fieldId];
          }
        }
      };

      const promise = run();
      inFlightRef.current[fieldId] = { signature, promise };
      return promise;
    },
    [autoSelectSingle, buildSignature, clearAfter, fetcher, fieldIndex, getCurrentValue, selectionsUpTo, setValue],
  );

  const handleSelect = useCallback(
    async (fieldId: string, option: CmdbOption | null): Promise<void> => {
      const prev = getCurrentValue(fieldId);
      const prevVal = prev?.value || '';
      const nextVal = option?.value || '';
      const hasChanged = prevVal !== nextVal;

      // Only clear / overwrite when the effective selection really changes.
      if (hasChanged) {
        setValue(fieldId, option || null);
        clearAfter(fieldId);
      }

      // Preload next field options.
      const idx = fieldIndex(fieldId);
      const nextId = idx >= 0 ? orderRef.current[idx + 1] : null;
      if (nextId) {
        const nextSelections = selectionsUpTo(nextId);
        if (option) nextSelections[fieldId] = option;
        await loadOptionsFor(nextId, nextSelections);
      }
    },
    [clearAfter, fieldIndex, loadOptionsFor, selectionsUpTo, setValue, getCurrentValue],
  );

  // Prefetch first field options when order/userCtx changes (cache prevents refetch).
  useEffect(() => {
    const firstId = orderRef.current && orderRef.current.length > 0 ? orderRef.current[0] : null;
    if (firstId) {
      loadOptionsFor(firstId);
    }
  }, [loadOptionsFor, fieldsOrder, userCtx]);

  // Enablement: a field is enabled only if all previous fields are answered.
  const isPrevAnswered = useCallback((fieldId: string) => {
    const idx = fieldIndex(fieldId);
    if (idx <= 0) return true;
    for (let i = 0; i < idx; i += 1) {
      const prevId = orderRef.current[i];
      const v = getCurrentValue(prevId);
      if (!v || (!v.value && !v.label)) return false;
    }
    return true;
  }, [fieldIndex, getCurrentValue]);

  const enabledByField = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const fid of orderRef.current) {
      map[fid] = isPrevAnswered(fid);
    }
    return map;
  }, [isPrevAnswered]);

  const api = useMemo<UseCmdbChainApi>(() => ({
    optionsByField,
    loadingField,
    loadOptionsFor,
    handleSelect,
    clearAfter,
    enabledByField,
    isFieldEnabled: (fieldId: string) => Boolean(enabledByField?.[fieldId]),
  }), [clearAfter, handleSelect, loadOptionsFor, loadingField, optionsByField, enabledByField]);

  return api;
}

export default useCmdbChain;
